import { Injectable } from '@angular/core';
import { ApiService, HttpMethod } from '../../shared/services/api/api.service';
import { FileReviewData, SeparatedDocument, SeparatedDocPage, ApprovalResponse, ApprovalPage } from '../models/file-review.model';

@Injectable({
  providedIn: 'root'
})
export class FileReviewService {

  private readonly baseUrl = 'multipart'; // Base route for the multipart API
  private readonly approvalBaseUrl = 'approval'; // Base route for the approval API

  constructor(private apiService: ApiService) {}

  /**
   * Gets the approval data for a given document
   * @param documentId - The parent document ID (e.g., "10508c3c-48ad-41d8-ac0d-4fd1a0e9bdd5")
   *                     This is the ID used in the route: /approval/:documentId
   * @returns Promise with approval response containing parent document, child documents, and pages
   */
  async getApprovalData(documentId: string): Promise<ApprovalResponse> {
    return this.apiService.call<ApprovalResponse>(
      HttpMethod.GET,
      `${this.approvalBaseUrl}/${documentId}`
    );
  }

  /**
   * Normalizes API status values to match the MultipartDocumentStatus enum
   * @param apiStatus - The status value from the API
   * @returns Normalized status string
   */
  private normalizeStatus(apiStatus: string): string {
    // Map API status values to enum values
    const statusMap: Record<string, string> = {
      'pending_separation_review': 'separation_review',
      'approved_separation_review': 'separation_approved',
      'separation_review': 'separation_review',
      'separation_approved': 'separation_approved',
      'separation_completed': 'separation_completed',
      'separation_processing': 'separation_processing',
      'uploaded': 'uploaded',
      'processing': 'processing',
      'pending_review': 'pending_review',
      'approved': 'approved'
    };

    return statusMap[apiStatus] || apiStatus;
  }

  /**
   * Transforms the approval API response to the format expected by the component
   * @param apiResponse - The approval response from the API
   * @returns FileReviewData object for component use
   * 
   * Note: The child document IDs (e.g., "10508c3c-48ad-41d8-ac0d-4fd1a0e9bdd5-child-1") 
   * are used to associate pages with their respective child documents via the pageIds array.
   */
  async mapApprovalResponseToFileReviewData(apiResponse: ApprovalResponse): Promise<FileReviewData> {
    // Create a map of pageId to page data for quick lookup
    const pageMap = new Map<string, ApprovalPage>();
    apiResponse.pages.forEach(page => {
      pageMap.set(page.pageId, page);
    });

    // Build separated documents from child documents
    // Each child document has a childDocumentId and an array of pageIds that associate pages to it
    const separated_documents: SeparatedDocument[] = apiResponse.childDocuments.map((childDoc, index) => {
      // Get pages for this child document by looking up pageIds, sorted by pageDisplayOrder
      const childPages = childDoc.pageIds
        .map(pageId => pageMap.get(pageId))
        .filter((page): page is ApprovalPage => page !== undefined)
        .sort((a, b) => a.pageDisplayOrder - b.pageDisplayOrder);

      // Transform to SeparatedDocPage format
      const pages: SeparatedDocPage[] = childPages.map((page) => {
        return {
          page_id: page.pageId,
          page_number: page.pageDisplayOrder,
          preview_url: '' // Will be loaded lazily when page is displayed
        };
      });

      return {
        doc_id: childDoc.childDocumentId,
        doc_name: childDoc.childDocumentId, // Internal name for API calls
        display_name: `File ${index + 1}`, // User-friendly display name
        page_count: pages.length,
        pages: pages,
        is_approved: false
      };
    });

    const total_pages = apiResponse.parentDocument.totalPages;
    const approved_count = separated_documents.filter(doc => doc.is_approved).length;

    // Normalize the status to match enum values
    const normalizedStatus = this.normalizeStatus(apiResponse.parentDocument.parentDocumentStatus);

    return {
      file_id: apiResponse.parentDocument.documentId,
      file_name: apiResponse.parentDocument.originalFilename,
      total_pages: total_pages,
      separated_documents: separated_documents,
      approved_count: approved_count,
      total_count: separated_documents.length,
      status: normalizedStatus
    };
  }

  /**
   * Gets the file review data by fetching and transforming the approval data
   * @param documentId - The parent document ID (e.g., "10508c3c-48ad-41d8-ac0d-4fd1a0e9bdd5")
   *                     This is passed to the /approval/:documentId endpoint
   * @returns Promise with FileReviewData object
   */
  async getFileReviewData(documentId: string): Promise<FileReviewData> {
    const approvalData = await this.getApprovalData(documentId);
    return this.mapApprovalResponseToFileReviewData(approvalData);
  }

  /**
   * Updates the page structure (reordering pages within a folder)
   * @param fileId - The ID of the file
   * @param affectedFolders - Array of folders with updated page orders
   * @returns Promise with update result
   */
  async updatePageStructure(
    fileId: string,
    affectedFolders: Array<{
      folderName: string;
      pages: Array<{ parentDocumentPageId: string; pageOrder: number }>;
    }>
  ): Promise<{ message: string; foldersUpdated: number; pagesUpdated: number }> {
    return this.apiService.call<{ message: string; foldersUpdated: number; pagesUpdated: number }>(
      HttpMethod.PUT,
      `${this.baseUrl}/${fileId}/update-structure`,
      { affectedFolders }
    );
  }

  /**
   * Renames a document (folder/file)
   * @param fileId - The ID of the file
   * @param oldDocumentName - The current document name
   * @param newDocumentName - The new document name
   * @returns Promise with rename result
   */
  async renameDocument(
    fileId: string,
    oldDocumentName: string,
    newDocumentName: string
  ): Promise<{ message: string; pagesUpdated: number; filesMovedInS3: number }> {
    return this.apiService.call<{ message: string; pagesUpdated: number; filesMovedInS3: number }>(
      HttpMethod.PUT,
      `${this.baseUrl}/${fileId}/category`,
      { 
        oldCategoryName: oldDocumentName,
        newCategoryName: newDocumentName
      }
    );
  }

  /**
   * Creates a new document (empty folder/file)
   * @param fileId - The ID of the file
   * @param documentName - The name of the new document
   * @returns Promise with creation result
   */
  async createDocument(
    fileId: string,
    documentName: string
  ): Promise<{ message: string; categoryName: string }> {
    return this.apiService.call<{ message: string; categoryName: string }>(
      HttpMethod.POST,
      `${this.baseUrl}/${fileId}/category`,
      { 
        categoryName: documentName
      }
    );
  }

  /**
   * Gets a presigned URL for accessing a specific page
   * @param fileId - The ID of the file
   * @param pageId - The ID of the page
   * @param mode - Access mode: 'view' or 'download' (defaults to 'view')
   * @returns Promise with presigned URL and expiration time
   */
  async getPageAccess(
    fileId: string,
    pageId: string,
    mode: 'view' | 'download' = 'view'
  ): Promise<{ presignedUrl: string; expiresIn: number }> {
    return this.apiService.call<{ presignedUrl: string; expiresIn: number }>(
      HttpMethod.GET,
      `${this.baseUrl}/access/${fileId}/page/${encodeURIComponent(pageId)}?mode=${mode}`
    );
  }

  /**
   * Submits the final separation review structure
   * @param documentId - The parent document ID
   * @param childDocuments - Array of child documents with their page IDs in final order
   * @returns Promise with submission result
   */
  async submitSeparationReview(
    documentId: string,
    childDocuments: Array<{
      childDocumentId: string;
      pageIds: string[];
    }>
  ): Promise<any> {
    return this.apiService.call<any>(
      HttpMethod.POST,
      `${this.approvalBaseUrl}/separation-review/${documentId}`,
      { childDocuments }
    );
  }
}
